import type { ApiError, Participant, ServerStatus, SessionNotice } from '@crowdset/contracts';
import { create } from 'zustand';
import type { RoomData } from './reducer';

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'joining'
  | 'live'
  | 'reconnecting'
  | 'offline'
  | 'ended' // stopped by a session notice (kicked, banned, moved…)
  | 'error'; // join refused (full, denied, unavailable…)

export interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'warn' | 'error' | 'ok';
}

interface RoomStore {
  slug: string | null;
  status: ConnectionStatus;
  error: ApiError | null;
  notice: SessionNotice | null;
  serverStatus: ServerStatus | null;
  data: RoomData | null;
  /** Set when the last snapshot/event arrived (local ms) — for "stale" indicators. */
  lastSyncAt: number | null;
  toasts: Toast[];
  set(p: Partial<Omit<RoomStore, 'set' | 'toast' | 'dismiss' | 'setParticipants'>>): void;
  setParticipants(roomId: string, participants: Participant[]): void;
  toast(text: string, tone?: Toast['tone']): void;
  dismiss(id: number): void;
}

let toastId = 0;

export const useRoomStore = create<RoomStore>()((set, get) => ({
  slug: null,
  status: 'idle',
  error: null,
  notice: null,
  serverStatus: null,
  data: null,
  lastSyncAt: null,
  toasts: [],
  set: (p) => set(p),
  setParticipants: (roomId, participants) => {
    const d = get().data;
    if (!d || d.room.id !== roomId) return;
    set({ data: { ...d, participants } });
  },
  toast: (text, tone = 'info') => {
    const id = ++toastId;
    set((s) => ({ toasts: [...s.toasts.slice(-3), { id, text, tone }] }));
    setTimeout(() => get().dismiss(id), tone === 'error' ? 7000 : 4500);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
